import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';

const API_BASE = '/api/v1/marketing';
const getToken = () => localStorage.getItem('token');

const api = async (path, opts = {}) => {
    const res = await fetch(`${API_BASE}${path}`, {
        ...opts,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}`, ...opts.headers }
    });
    return res.json();
};

const MarketingContactList = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [list, setList] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(new Set());
    const [editing, setEditing] = useState(null);
    const [editValues, setEditValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);

    const notify = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

    const fetchContacts = useCallback(async (p = 1) => {
        setLoading(true);
        const data = await api(`/contacts/lists/${id}?page=${p}&limit=100`);
        if (data.success) {
            setList(data.list);
            setContacts(data.contacts);
            setPagination(data.pagination);
            setPage(p);
            setSelected(new Set());
        }
        setLoading(false);
    }, [id]);

    useEffect(() => { fetchContacts(1); }, [fetchContacts]);

    const filtered = contacts.filter(c => {
        if (!search) return true;
        return (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
               (c.phone || '').includes(search) ||
               (c.company_name || '').toLowerCase().includes(search.toLowerCase());
    });

    const toggleSelect = (cId) => {
        const next = new Set(selected);
        if (next.has(cId)) next.delete(cId); else next.add(cId);
        setSelected(next);
    };

    const toggleAll = () => {
        if (selected.size === filtered.length) setSelected(new Set());
        else setSelected(new Set(filtered.map(c => c.id)));
    };

    const deleteContact = async (cId) => {
        if (!window.confirm('حذف هذا الرقم؟')) return;
        const data = await api(`/contacts/${cId}`, { method: 'DELETE' });
        if (data.success) {
            setContacts(prev => prev.filter(c => c.id !== cId));
            const next = new Set(selected);
            next.delete(cId);
            setSelected(next);
            notify('تم الحذف');
        }
    };

    const bulkDelete = async () => {
        if (!selected.size) return;
        if (!window.confirm(`حذف ${selected.size} رقم؟`)) return;
        const data = await api('/contacts/bulk-delete', { method: 'POST', body: JSON.stringify({ ids: [...selected] }) });
        if (data.success) {
            notify(`تم حذف ${data.deleted} رقم`);
            fetchContacts(page);
        }
    };

    const startEdit = (c) => {
        setEditing(c.id);
        setEditValues({ name: c.name || '', phone: c.phone || '', company_name: c.company_name || '' });
    };

    const saveEdit = async (cId) => {
        const data = await api(`/contacts/${cId}`, { method: 'PUT', body: JSON.stringify(editValues) });
        if (data.success) {
            setContacts(prev => prev.map(c => c.id === cId ? { ...c, ...editValues } : c));
            setEditing(null);
            notify('تم التحديث');
        }
    };

    const cancelEdit = () => { setEditing(null); setEditValues({}); };

    if (loading && !list) return (
        <div style={S.wrap}>
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
                <div style={{ width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto' }} />
                <p style={{ marginTop: 16 }}>جاري التحميل...</p>
            </div>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
    );

    return (
        <Layout>
        <div style={S.wrap}>
            <style>{`
                @keyframes spin{to{transform:rotate(360deg)}}
                input[type=checkbox]{width:16px;height:16px;accent-color:#6366f1;cursor:pointer}
                table tbody tr:hover{background:#c7d2fe!important} table tbody tr:hover td{background:#c7d2fe!important;color:#1e1b4b!important}
                tr.row-selected td{background:rgba(99,102,241,0.18)!important}
                button:hover:not(:disabled){filter:brightness(.92)}
                button:disabled{cursor:not-allowed;opacity:.5}
            `}</style>

            {toast && (
                <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', padding: '10px 24px', borderRadius: 10, fontSize: 14, fontWeight: 500, zIndex: 9999, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', background: toast.type === 'error' ? '#fee2e2' : '#dcfce7', color: toast.type === 'error' ? '#dc2626' : '#16a34a' }}>
                    {toast.msg}
                </div>
            )}

            {/* Header */}
            <div style={S.header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <button onClick={() => navigate('/marketing')} style={S.backBtn}>→</button>
                    <div>
                        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1e293b' }}>{list?.name || 'القائمة'}</h1>
                        <p style={{ margin: '2px 0 0', fontSize: 13, color: '#94a3b8', maxWidth: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {(list?.description || '').slice(0, 80)}{(list?.description || '').length > 80 ? '...' : ''} {pagination ? ` • ${pagination.total} رقم` : ''}
                        </p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 10, fontSize: 13 }}>
                    {list && <>
                        <span style={{ background: '#16a34a', color: '#fff', padding: '4px 12px', borderRadius: 8, fontWeight: 600 }}>{list.valid_contacts} صالح</span>
                        <span style={{ background: '#dc2626', color: '#fff', padding: '4px 12px', borderRadius: 8, fontWeight: 600 }}>{list.invalid_contacts} غير صالح</span>
                        <span style={{ background: '#d97706', color: '#fff', padding: '4px 12px', borderRadius: 8, fontWeight: 600 }}>{list.duplicate_contacts} مكرر</span>
                    </>}
                </div>
            </div>

            {/* Toolbar */}
            <div style={S.toolbar}>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="بحث بالاسم أو الرقم أو الشركة..." style={S.searchInput} />
                {selected.size > 0 && (
                    <button onClick={bulkDelete} style={S.dangerBtn}>🗑️ حذف المحدد ({selected.size})</button>
                )}
                <button onClick={() => fetchContacts(page)} style={S.refreshBtn}>🔄 تحديث</button>
            </div>

            {/* Table */}
            <div style={S.tableWrap}>
                <table style={S.table}>
                    <thead>
                        <tr>
                            <th style={{ ...S.th, width: 40 }}>
                                <input type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length} onChange={toggleAll} />
                            </th>
                            <th style={{ ...S.th, width: 50 }}>#</th>
                            <th style={S.th}>الاسم</th>
                            <th style={S.th}>رقم الواتساب</th>
                            <th style={S.th}>الشركة</th>
                            <th style={{ ...S.th, width: 70 }}>الحالة</th>
                            <th style={{ ...S.th, width: 110, textAlign: 'center' }}>إجراءات</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((c, i) => (
                            <tr key={c.id} className={selected.has(c.id) ? 'row-selected' : ''}>
                                <td style={S.td}><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} /></td>
                                <td style={{ ...S.td, color: '#94a3b8', fontSize: 12 }}>{((page - 1) * 100) + i + 1}</td>
                                <td style={S.td}>
                                    {editing === c.id ? (
                                        <input value={editValues.name} onChange={e => setEditValues({ ...editValues, name: e.target.value })}
                                            onKeyDown={e => e.key === 'Enter' && saveEdit(c.id)} style={S.editInput} autoFocus />
                                    ) : (
                                        <span style={{ fontWeight: c.name ? 500 : 400, color: c.name ? '#1e293b' : '#cbd5e1' }}>{c.name || '—'}</span>
                                    )}
                                </td>
                                <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12, direction: 'ltr', textAlign: 'left' }}>
                                    {editing === c.id ? (
                                        <input value={editValues.phone} onChange={e => setEditValues({ ...editValues, phone: e.target.value })}
                                            onKeyDown={e => e.key === 'Enter' && saveEdit(c.id)} style={{ ...S.editInput, direction: 'ltr', fontFamily: 'monospace' }} />
                                    ) : c.phone}
                                </td>
                                <td style={S.td}>
                                    {editing === c.id ? (
                                        <input value={editValues.company_name} onChange={e => setEditValues({ ...editValues, company_name: e.target.value })}
                                            onKeyDown={e => e.key === 'Enter' && saveEdit(c.id)} style={S.editInput} />
                                    ) : (
                                        <span style={{ color: c.company_name ? '#475569' : '#cbd5e1' }}>{c.company_name || '—'}</span>
                                    )}
                                </td>
                                <td style={S.td}>
                                    {c.phone_valid ? <span style={{ color: '#22c55e', fontSize: 12, fontWeight: 600 }}>✓ صالح</span> : <span style={{ color: '#ef4444', fontSize: 12, fontWeight: 600 }}>✗ غير صالح</span>}
                                </td>
                                <td style={{ ...S.td, textAlign: 'center' }}>
                                    {editing === c.id ? (
                                        <>
                                            <button onClick={() => saveEdit(c.id)} style={S.actionBtn} title="حفظ">✅</button>
                                            <button onClick={cancelEdit} style={S.actionBtn} title="إلغاء">❌</button>
                                        </>
                                    ) : (
                                        <>
                                            <button onClick={() => startEdit(c)} style={S.actionBtn} title="تعديل">✏️</button>
                                            <button onClick={() => deleteContact(c.id)} style={{ ...S.actionBtn, color: '#ef4444' }} title="حذف">🗑️</button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {!filtered.length && !loading && (
                    <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>{search ? 'لا توجد نتائج للبحث' : 'لا توجد جهات اتصال'}</div>
                )}
                {loading && <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>جاري التحميل...</div>}
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
                <div style={S.pag}>
                    <button disabled={page <= 1} onClick={() => fetchContacts(1)} style={S.pageBtn}>« الأولى</button>
                    <button disabled={page <= 1} onClick={() => fetchContacts(page - 1)} style={S.pageBtn}>‹ السابق</button>
                    <span style={{ fontSize: 13, color: '#64748b', padding: '0 12px' }}>صفحة {page} من {pagination.pages} ({pagination.total} رقم)</span>
                    <button disabled={page >= pagination.pages} onClick={() => fetchContacts(page + 1)} style={S.pageBtn}>التالي ›</button>
                    <button disabled={page >= pagination.pages} onClick={() => fetchContacts(pagination.pages)} style={S.pageBtn}>الأخيرة »</button>
                </div>
            )}
        </div>
        </Layout>
    );
};

export default MarketingContactList;

const S = {
    wrap: { fontFamily: "'IBM Plex Sans Arabic',sans-serif", direction: 'rtl', padding: 0, color: '#1e293b', minHeight: '100%' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, padding: '20px 24px 0', flexWrap: 'wrap', gap: 12 },
    backBtn: { width: 36, height: 36, borderRadius: 8, border: '1.5px solid #6366f1', background: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 18, color: '#fff', fontWeight: 700 },
    toolbar: { display: 'flex', alignItems: 'center', gap: 10, padding: '0 24px', marginBottom: 16, flexWrap: 'wrap' },
    searchInput: { flex: 1, minWidth: 250, padding: '10px 16px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none', direction: 'rtl', background: '#fff' },
    dangerBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', border: 'none', borderRadius: 8, background: '#ef4444', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    refreshBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: '1.5px solid #6366f1', borderRadius: 8, background: '#6366f1', fontSize: 13, cursor: 'pointer', color: '#fff', fontWeight: 600 },
    tableWrap: { background: '#fff', borderRadius: 14, margin: '0 24px', overflow: 'auto', maxHeight: 'calc(100vh - 260px)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { padding: '12px 14px', background: '#f8f7fd', color: '#64748b', fontWeight: 600, fontSize: 12, textAlign: 'right', borderBottom: '2px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 1 },
    td: { padding: '10px 14px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
    editInput: { width: '100%', padding: '4px 8px', border: '1.5px solid #6366f1', borderRadius: 6, fontSize: 13, outline: 'none', direction: 'rtl' },
    actionBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: 15, lineHeight: 1 },
    pag: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, padding: '16px 24px', direction: 'ltr' },
    pageBtn: { padding: '6px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, color: '#475569' },
};
